import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Text, Stack, List, Grid, Button, Message, Link } from 'basis';
import { useTheme } from '@emotion/react';
import { useStoreValue, REFERRED } from 'store';
import { DocUpload } from 'doc-upload';
import { LayoutPage, LayoutContent, YellowMessage, SaveAndCompleteLaterButton, StyledUL } from 'components';
import { generateUploadConfig, isDJ } from 'utils';
import { useDataLayerAndAnalyticsLinks, useErrorTracking } from 'hooks';
import { CHANNEL_INSTORE, OverviewSources } from '_config';

export function UploadIDDocuments() {
  const [store, updateStore] = useStoreValue();
  const { handleErrorTracking } = useErrorTracking();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(store);
  const [uploading, setUploading] = useState(false);
  const history = useHistory();
  const theme = useTheme();

  const dmsChannel = store?.applyAndBuy.channel === CHANNEL_INSTORE ? 'INSTORE' : 'ONLINE';

  const methodsRef = useRef(null);
  const handleStartUpload = async () => {
    setUploading(true);
    await methodsRef.current?.startUpload?.();
    setUploading(false);
  };

  const handleDocUploadEvent = evt => {
    switch (evt.type) {
      case 'ERROR':
        handleErrorTracking(evt.error?.message ?? evt.error);
        break;
      case 'COMPLETE':
        setUploading(false);
        trackEventSender({
          category: 'application',
          action: 'application-navigation',
          location: 'id-doc-upload',
          label: 'Upload ID documents',
        });
        updateStore({
          idDocumentUpdated: true,
          activeStep: '/outcome',
          applicationStatus: REFERRED,
        });
        history.push('/outcome');
        break;
      case 'INIT':
        methodsRef.current = {
          startUpload: evt.data.startUpload,
        };
        break;
      default:
        break;
    }
  };
  const config = generateUploadConfig({
    applicationRef: store.applicationRef,
    channel: dmsChannel,
    documentSubType: 'OTHER',
    handleDocUploadEvent,
    documentType: 'OTHER',
    theme: {
      textColor: isDJ() ? theme.colors.textBodyColor : theme.colors.indigo,
      linkColor: theme.a.color,
    },
  });

  return (
    <LayoutPage>
      <LayoutContent>
        {dmsChannel === 'INSTORE' && (
          <YellowMessage>
            <Message testId="yellow-message" severity="warning-or-significant">
              If you are in the store, the salesperson will be able to help you with verifying your identity.
            </Message>
          </YellowMessage>
        )}
        <Stack gap="6">
          <Text>
            Please upload one <strong>certified</strong> document from the list below:
          </Text>
          <StyledUL paddingLeft="0" pointColor="black" pointSize="0.3em">
            <List>
              <List.Item>New Zealand Driver Licence</List.Item>
              <List.Item>New Zealand Passport</List.Item>
              <List.Item>Australian Passport</List.Item>
              <List.Item>
                18+ Card with a photo (along with a New Zealand Birth Certificate or New Zealand Citizenship
                Certificate)
              </List.Item>
              <List.Item>
                SuperGold Card with a photo (along with a New Zealand Birth Certificate or New Zealand Citizenship
                Certificate)
              </List.Item>
              <List.Item>Cook Islands/Tokelau/Niue Passport</List.Item>
              <List.Item>New Zealand Firearms Licence</List.Item>
              <List.Item>Other Passport</List.Item>
            </List>
          </StyledUL>
          <Text>
            You’ll need to take <strong>the original document and a copy</strong> to be certified by a Trusted Referee
            in New Zealand. Our list of Trusted Referees include:
          </Text>
          <StyledUL paddingLeft="0" pointColor="black" pointSize="0.3em">
            <List>
              <List.Item>Justice of the Peace</List.Item>
              <List.Item>Registered Medical Doctor</List.Item>
              <List.Item>Registered Teacher</List.Item>
              <List.Item>
                Lawyer (as defined in the{' '}
                <Link href={OverviewSources.NZ.lawyers_conveyancers_act.url} newTab>
                  Lawyers and Conveyancers Act 2006
                </Link>
                )
              </List.Item>
              <List.Item>Member of Parliament</List.Item>
              <List.Item>Chartered Accountant</List.Item>
            </List>
          </StyledUL>
          <Text>
            Once your documents are uploaded, our team will review them and contact you within{' '}
            <strong>two business days</strong>.
          </Text>
          <DocUpload {...config} />
          <Grid>
            <Grid.Item alignItems="center" colSpan="all">
              <Grid rowsGap="4" colsGap="2">
                <Grid.Item colSpan="all" colSpan-sm="0-4" rowSpan-sm="0">
                  <Grid>
                    <Button
                      width="100%"
                      type="button"
                      disabled={uploading}
                      onClick={handleStartUpload}
                      loading={uploading}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid.Item>
                <Grid.Item colSpan="all" colSpan-sm="5-7" alignItems="center">
                  <Grid>
                    <SaveAndCompleteLaterButton />
                  </Grid>
                </Grid.Item>
              </Grid>
            </Grid.Item>
          </Grid>
        </Stack>
      </LayoutContent>
    </LayoutPage>
  );
}
